import { buildPublicContentPath, resolvePublicContentKind } from './contentRoutes';

const SITE_URL = 'https://suarte.art';
const DEFAULT_IMAGE = 'https://imagedelivery.net/hAeIC__6Aj746x0RFU1joA/b640dc78-9146-4a0e-5d34-1d25ced2ce00/public';
const SUPPORTED_LOCALES = ['en', 'es', 'de', 'it', 'fr', 'pt'];
const CONTENT_DATE_LOCALES = {
  en: 'en-US',
  es: 'es-ES',
  de: 'de-DE',
  it: 'it-IT',
  fr: 'fr-FR',
  pt: 'pt-PT',
};
const LOCALIZED_CONTENT_TITLE_TEMPLATES = {
  es: {
    article: '{articleTitle} por {publicProfileName} | Suarte',
    post: 'Publicación de {publicProfileName}: "{postDescription}" | Suarte',
  },
  de: {
    article: '{articleTitle} von {publicProfileName} | Suarte',
    post: 'Beitrag von {publicProfileName}: "{postDescription}" | Suarte',
  },
  it: {
    article: '{articleTitle} di {publicProfileName} | Suarte',
    post: 'Pubblicazione di {publicProfileName}: "{postDescription}" | Suarte',
  },
  fr: {
    article: '{articleTitle} par {publicProfileName} | Suarte',
    post: 'Publication de {publicProfileName} : "{postDescription}" | Suarte',
  },
  pt: {
    article: '{articleTitle} por {publicProfileName} | Suarte',
    post: 'Publicação de {publicProfileName}: "{postDescription}" | Suarte',
  },
};
const LOCALIZED_CONTENT_DESCRIPTION_TEMPLATES = {
  es: {
    article: '{articleShortDescription}',
    post: 'Publicado el {datePublishedLabel}. {publicProfileName} escribió: "{postDescription}"',
  },
  de: {
    article: '{articleShortDescription}',
    post: 'Veröffentlicht am {datePublishedLabel}. {publicProfileName} schrieb: "{postDescription}"',
  },
  it: {
    article: '{articleShortDescription}',
    post: 'Pubblicato il {datePublishedLabel}. {publicProfileName} ha scritto: "{postDescription}"',
  },
  fr: {
    article: '{articleShortDescription}',
    post: 'Publié le {datePublishedLabel}. {publicProfileName} a écrit : "{postDescription}"',
  },
  pt: {
    article: '{articleShortDescription}',
    post: 'Publicado em {datePublishedLabel}. {publicProfileName} escreveu: "{postDescription}"',
  },
};

function normalizeText(value) {
  if (typeof value !== 'string') {
    return '';
  }

  return value.replace(/\s+/g, ' ').trim();
}

function stripHtml(value) {
  return normalizeText(String(value || '').replace(/<[^>]*>/g, ' '));
}

function normalizeLocale(locale) {
  const normalized = normalizeText(locale).toLowerCase();
  if (!normalized) {
    return 'en';
  }

  const shortCode = normalized.split('-')[0];
  return SUPPORTED_LOCALES.includes(shortCode) ? shortCode : 'en';
}

function truncateTextWithParens(value, maxLength = 160) {
  const normalized = normalizeText(value);
  const suffix = ' (...)';
  if (!normalized || normalized.length <= maxLength) {
    return normalized;
  }

  const shortened = normalized.slice(0, Math.max(0, maxLength - suffix.length)).trim();
  const lastSpace = shortened.lastIndexOf(' ');
  const safeValue = lastSpace > 40 ? shortened.slice(0, lastSpace) : shortened;
  return `${safeValue.trim()}${suffix}`;
}

function resolveCoverImage(content = {}) {
  const coverId = normalizeText(content?.post_container?.post_cover?.image_id);
  if (coverId) {
    return `https://imagedelivery.net/hAeIC__6Aj746x0RFU1joA/${coverId}/w=1200`;
  }

  const pictureGroups = Array.isArray(content?.post_container?.post_pictures)
    ? content.post_container.post_pictures
    : [];
  const firstPicture = pictureGroups[0]?.pictures?.[0]?.image_id;
  if (firstPicture) {
    return `https://imagedelivery.net/hAeIC__6Aj746x0RFU1joA/${firstPicture}/w=1200`;
  }

  return DEFAULT_IMAGE;
}

function resolveBodyText(content = {}) {
  const blocks = Array.isArray(content?.post_container?.post_texts)
    ? content.post_container.post_texts
    : [];

  return blocks
    .map((block) => stripHtml(block?.content))
    .filter(Boolean)
    .join(' ');
}

function resolveOwner(content = {}) {
  return content?.gallery || content?.artist || content?.collector || null;
}

function resolveOwnerName(content = {}) {
  const owner = resolveOwner(content);
  return normalizeText(owner?.user_name || owner?.user_username) || 'Suarte';
}

function resolveOwnerUsername(content = {}) {
  const owner = resolveOwner(content);
  return normalizeText(owner?.user_username);
}

function resolveDatePublishedIso(content = {}) {
  const value = content?.publishedAt || content?.createdAt || null;
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

function resolveDatePublishedLabel(content = {}, locale = 'en') {
  const iso = resolveDatePublishedIso(content);
  if (!iso) {
    return '';
  }

  return new Intl.DateTimeFormat(CONTENT_DATE_LOCALES[normalizeLocale(locale)] || 'en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(iso));
}

export function buildContentSeoMetadata(content = {}) {
  if (content?.__seo) {
    return content.__seo;
  }

  const kind = resolvePublicContentKind(content);
  const locale = normalizeLocale(content?.__seoLocale);
  const ownerName = resolveOwnerName(content);
  const explicitTitle = normalizeText(content?.post_container?.post_title);
  const explicitDescription = normalizeText(content?.post_container?.post_description);
  const canonicalUrl = `${SITE_URL}${buildPublicContentPath(content, kind)}`;
  const bodyText = resolveBodyText(content);
  const descriptionExcerpt = truncateTextWithParens(explicitDescription || bodyText, 140);
  const publishedLabel = resolveDatePublishedLabel(content, locale);
  const headline = explicitTitle || `${ownerName} on Suarte`;
  const templateVariables = {
    publicProfileName: ownerName,
    articleTitle: explicitTitle || ownerName,
    articleShortDescription: descriptionExcerpt,
    postDescription: descriptionExcerpt,
    datePublishedLabel: publishedLabel,
  };
  const titleTemplate = normalizeLocale(locale) === 'en'
    ? (kind === 'article'
      ? '{articleTitle} by {publicProfileName} | Suarte'
      : 'Post by {publicProfileName}, "{postDescription}" | Suarte')
    : LOCALIZED_CONTENT_TITLE_TEMPLATES[locale]?.[kind] || '';
  const descriptionTemplate = normalizeLocale(locale) === 'en'
    ? (kind === 'article'
      ? '{articleShortDescription}'
      : 'Added on {datePublishedLabel}. {publicProfileName} wrote "{postDescription}"')
    : LOCALIZED_CONTENT_DESCRIPTION_TEMPLATES[locale]?.[kind] || '';
  const applyTemplate = (template, maxLength) =>
    truncateTextWithParens(
      normalizeText(template).replace(/\{([a-zA-Z0-9]+)\}/g, (_match, token) => {
        const value = templateVariables[token];
        return typeof value === 'string' && value.trim() ? value.trim() : '';
      }),
      maxLength
    );
  const metaDescription = kind === 'article'
    ? applyTemplate(descriptionTemplate || '{articleShortDescription}', 160) || descriptionExcerpt
    : applyTemplate(descriptionTemplate || 'Added on {datePublishedLabel}. {publicProfileName} wrote "{postDescription}"', 160)
      || truncateTextWithParens(
        descriptionExcerpt
          ? `${publishedLabel ? `Added on ${publishedLabel}. ` : ''}${ownerName} wrote "${descriptionExcerpt}"`
          : `${publishedLabel ? `Added on ${publishedLabel}. ` : ''}${ownerName} shared a post on Suarte.${bodyText ? ` ${truncateTextWithParens(bodyText, 120)}` : ''}`,
        160
      );
  const title = kind === 'article'
    ? applyTemplate(titleTemplate || '{articleTitle} by {publicProfileName} | Suarte', 110) || `${explicitTitle || ownerName} by ${ownerName} | Suarte`
    : descriptionExcerpt
      ? applyTemplate(titleTemplate || 'Post by {publicProfileName}, "{postDescription}" | Suarte', 110)
        || truncateTextWithParens(`Post by ${ownerName}, "${descriptionExcerpt}" | Suarte`, 110)
      : `Post by ${ownerName} | Suarte`;

  return {
    indexable: true,
    type: kind,
    headline,
    title,
    metaDescription,
    canonicalUrl,
    robots: 'index,follow,max-image-preview:large',
    ogImage: resolveCoverImage(content),
    ogType: 'article',
    authorName: ownerName,
    authorUsername: resolveOwnerUsername(content),
    datePublished: resolveDatePublishedIso(content),
    dateModified: content?.updatedAt || content?.publishedAt || content?.createdAt || null,
  };
}

export function buildContentStructuredData(content = {}, metadata = {}) {
  const kind = resolvePublicContentKind(content);
  return {
    '@context': 'https://schema.org',
    '@type': kind === 'article' ? 'NewsArticle' : 'BlogPosting',
    headline: metadata.headline,
    description: metadata.metaDescription,
    image: [metadata.ogImage || DEFAULT_IMAGE],
    datePublished: metadata.datePublished || undefined,
    dateModified: metadata.dateModified || undefined,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': metadata.canonicalUrl,
    },
    author: {
      '@type': 'Person',
      name: metadata.authorName,
      url: metadata.authorUsername ? `${SITE_URL}/user/${metadata.authorUsername}` : undefined,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Suarte',
      url: SITE_URL,
    },
  };
}
